import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Close, Print } from '@mui/icons-material';
import { ECGPlot } from 'components';
import { ECGRecord } from './partner-ecg-view';
import { MV_PER_ROW, TIME_SCALE } from 'utils/constants';
import { useReactToPrint } from 'hooks';
import { getLocaleDate } from 'utils';
import { config } from 'packages/config';

// Helper function to format timestamp with local timezone
const formatTimestamp = (timestamp: string) => {
  return getLocaleDate(timestamp, 'MM/DD hh:mm a');
};

interface PartnerECGModalProps {
  open: boolean;
  onClose: () => void;
  ecgRecord: ECGRecord | null;
  partnerId: string;
  partnerApiKey: string;
}

interface WaveformData {
  samples: number[];
  sampleRate: number;
  duration: number;
  patientName?: string;
  timestamp?: string;
  heartRate?: number;
  assessment?: string;
  ecgLead?: string;
  submittedTimestamp?: string;
}

// Constants from shared-ecg
const PLOT_WIDTH = 4750;
const STROKE_COLOR = '#000000';
const GRID_COLOR = '#FF0000';
const PRINT_ECG_DURATION = 10;

export const PartnerECGModal: React.FC<PartnerECGModalProps> = ({
  open,
  onClose,
  ecgRecord,
  partnerId,
  partnerApiKey,
}) => {
  const [waveformData, setWaveformData] = useState<WaveformData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const plotRef = useRef<null | HTMLDivElement>(null);
  const printRef = useRef<null | HTMLDivElement>(null);

  const fetchWaveformData = useCallback(async () => {
    if (!ecgRecord || !partnerId || !partnerApiKey) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.ENV.API.EVERBEAT_BASE_URL}/partner/waveform/${partnerId}/${partnerApiKey}/${ecgRecord.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch waveform data: ${response.status}`);
      }

      const data = await response.json();

      // Debug log to see the response structure
      console.log('Partner waveform response:', data);
      console.log('Response type:', typeof data);
      console.log('Response keys:', Object.keys(data));

      // Parse the waveform data from the response
      // Check various possible field names for samples
      let samples = [];

      // Check if the entire response is an array (samples directly)
      if (Array.isArray(data)) {
        samples = data;
      } else if (data.samples) {
        if (Array.isArray(data.samples)) {
          samples = data.samples;
        } else if (typeof data.samples === 'string') {
          // Try to parse if it's a JSON string
          try {
            samples = JSON.parse(data.samples);
          } catch (e) {
            // If not JSON, might be comma-separated values
            samples = data.samples
              .split(',')
              .map(Number)
              .filter((n) => !isNaN(n));
          }
        }
      } else if (data.waveform && Array.isArray(data.waveform)) {
        samples = data.waveform;
      } else if (data.data && Array.isArray(data.data)) {
        samples = data.data;
      } else if (data.ecgSamples && Array.isArray(data.ecgSamples)) {
        samples = data.ecgSamples;
      } else if (typeof data === 'object') {
        // Look for any array property that might contain samples
        const arrayProps = Object.keys(data).filter((key) => Array.isArray(data[key]) && data[key].length > 100);
        if (arrayProps.length > 0) {
          console.log('Found array property:', arrayProps[0]);
          samples = data[arrayProps[0]];
        }
      }

      console.log('Samples array length:', samples.length);
      console.log('First 10 samples:', samples.slice(0, 10));

      const sampleRate = data.sampleRate || data.sample_rate || data.frequency || 512;

      // Check if samples need scaling (typical ECG samples are much larger integers)
      const sampleMin = Math.min(...samples);
      const sampleMax = Math.max(...samples);
      const sampleRange = sampleMax - sampleMin;

      console.log('Raw sample stats - min:', sampleMin, 'max:', sampleMax, 'range:', sampleRange);

      // Skip first 5 seconds (calibration period) for scaling calculations
      const calibrationSeconds = 5;
      const calibrationSamples = calibrationSeconds * sampleRate;
      const samplesAfterCalibration = samples.slice(calibrationSamples);

      console.log('Skipping calibration period:');
      console.log('Total samples:', samples.length);
      console.log('Calibration samples to skip:', calibrationSamples);
      console.log('Samples after calibration:', samplesAfterCalibration.length);

      // Calculate scaling based on data after calibration period
      const rawMin = Math.min(...samplesAfterCalibration);
      const rawMax = Math.max(...samplesAfterCalibration);
      const rawRange = rawMax - rawMin;

      console.log('ECG data stats (after calibration):');
      console.log('Raw min:', rawMin, 'Raw max:', rawMax, 'Raw range:', rawRange);

      // Target amplitude similar to sine wave (±500)
      const targetAmplitude = 500;
      const scaleFactor = rawRange > 0 ? (targetAmplitude * 2) / rawRange : 1;

      // Center and scale ALL samples using the calibration-corrected stats
      const finalSamples = samples.map((sample) => {
        const centered = sample - (rawMin + rawMax) / 2; // Center around zero

        return centered * scaleFactor; // Scale to target amplitude
      });

      console.log('Scaled ECG data stats:');
      console.log('Scale factor:', scaleFactor);
      console.log('Scaled min:', Math.min(...finalSamples));
      console.log('Scaled max:', Math.max(...finalSamples));
      console.log('First 10 scaled samples:', finalSamples.slice(0, 10));

      setWaveformData({
        samples: finalSamples, // Use scaled real ECG data
        sampleRate: sampleRate,
        duration: ecgRecord.duration,
        patientName: ecgRecord.patientName || 'Unknown Patient',
        timestamp: ecgRecord.createdAt,
        heartRate: data.heartRate || data.heart_rate || data.hr,
        assessment: data.assessment || data.diagnosis || 'Normal',
        ecgLead: ecgRecord.ecgLead || data.ecgLead || data.lead || 'Lead I',
        submittedTimestamp: data.submittedTimestamp || data.submitted_timestamp || ecgRecord.createdAt,
      });
    } catch (err) {
      console.error('Error fetching waveform data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch waveform data');
    } finally {
      setIsLoading(false);
    }
  }, [ecgRecord, partnerId, partnerApiKey]);

  useEffect(() => {
    if (open && ecgRecord) {
      fetchWaveformData();
    }
  }, [open, ecgRecord, fetchWaveformData]);

  // Use samples directly, they're already processed like study ECG
  const filteredSamples = useMemo(() => {
    if (!waveformData || !waveformData.samples || waveformData.samples.length === 0) {
      console.log('No waveform data or samples for display');

      return [];
    }

    console.log('Using processed samples for display - length:', waveformData.samples.length);
    console.log('First 10 display samples:', waveformData.samples.slice(0, 10));
    console.log(
      'Display sample range - min:',
      Math.min(...waveformData.samples),
      'max:',
      Math.max(...waveformData.samples),
    );

    return waveformData.samples;
  }, [waveformData]);

  // Calculate print samples based on scroll offset
  const printSamples = useMemo(() => {
    if (!filteredSamples.length || !waveformData) {
      return [];
    }

    const ECGDuration = filteredSamples.length / waveformData.sampleRate;
    const oneSecondOffsetInPixels = PLOT_WIDTH / ECGDuration;

    const firstSampleIndex = Math.floor((scrollOffset / oneSecondOffsetInPixels) * waveformData.sampleRate);
    const lastSampleIndex = firstSampleIndex + PRINT_ECG_DURATION * waveformData.sampleRate;

    return filteredSamples.slice(firstSampleIndex, lastSampleIndex);
  }, [waveformData?.sampleRate, filteredSamples, scrollOffset]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    setScrollOffset(e.currentTarget.scrollLeft);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page { size: landscape; margin: 0.5in; }`,
  });

  const handleClose = () => {
    setWaveformData(null);
    setError(null);
    setScrollOffset(0);
    onClose();
  };

  if (!ecgRecord) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
        <Box>
          <Typography variant='h6' component='div'>
            ECG Waveform
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {ecgRecord.patientName} - {formatTimestamp(ecgRecord.createdAt)} - {ecgRecord.ecgLead} - Duration:{' '}
            {Math.floor(ecgRecord.duration)}s
          </Typography>
        </Box>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading waveform data...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {waveformData && !isLoading && !error && (
          <Box>
            {/* ECG Details Section (like shared-ecg) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant='body1' sx={{ mb: 1 }}>
                Recorded: {getLocaleDate(waveformData.submittedTimestamp || waveformData.timestamp)}
              </Typography>
              <Box component='ul' sx={{ pl: 3, m: 0 }}>
                <Typography component='li' variant='body2'>
                  Name: {waveformData.patientName}
                </Typography>
                <Typography component='li' variant='body2'>
                  {waveformData.ecgLead}
                </Typography>
                <Typography component='li' variant='body2'>
                  Voltage Scale: {MV_PER_ROW} mV/division
                </Typography>
                <Typography component='li' variant='body2'>
                  Time Scale: {TIME_SCALE}
                </Typography>
                {waveformData.heartRate && (
                  <Typography component='li' variant='body2'>
                    Heart rate: {waveformData.heartRate} bpm
                  </Typography>
                )}
                <Typography component='li' variant='body2'>
                  Assessment: {waveformData.assessment}
                </Typography>
              </Box>
            </Box>

            {/* Main ECG Plot with scroll (like shared-ecg) */}
            <Box
              ref={plotRef}
              onScroll={handleScroll}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                maxWidth: '100%',
                mb: 3,
              }}
            >
              <ECGPlot
                strokeColor={STROKE_COLOR}
                samples={filteredSamples}
                decreaseWave={0}
                sampleRate={waveformData.sampleRate}
                durationToShow={undefined}
                millivolts={undefined}
                startTime={5}
                showGrid={true}
                signalStrokeWidth={1}
                smallGridStrokeWidth={0.21}
                bigGridStrokeWidth={0.4}
                physicalGridSquareSize={1}
                full={undefined}
                backgroundFill='#fff'
                cellColor={GRID_COLOR}
                plotWidth={PLOT_WIDTH}
                plotHeight={400}
              />
            </Box>

            {/* Print button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant='contained' startIcon={<Print />} onClick={handlePrint} sx={{ mb: 2 }}>
                Print ECG
              </Button>
            </Box>

            {/* Hidden print container */}
            <Box
              ref={printRef}
              sx={{
                display: 'none',
                '@media print': {
                  display: 'block',
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Typography variant='h6' sx={{ mb: 2 }}>
                  ECG Report
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant='body1' sx={{ mb: 1 }}>
                    Recorded: {getLocaleDate(waveformData.submittedTimestamp || waveformData.timestamp)}
                  </Typography>
                  <Box component='ul' sx={{ pl: 3, m: 0 }}>
                    <Typography component='li' variant='body2'>
                      Name: {waveformData.patientName}
                    </Typography>
                    <Typography component='li' variant='body2'>
                      {waveformData.ecgLead}
                    </Typography>
                    <Typography component='li' variant='body2'>
                      Voltage Scale: {MV_PER_ROW} mV/division
                    </Typography>
                    <Typography component='li' variant='body2'>
                      Time Scale: {TIME_SCALE}
                    </Typography>
                    {waveformData.heartRate && (
                      <Typography component='li' variant='body2'>
                        Heart rate: {waveformData.heartRate} bpm
                      </Typography>
                    )}
                    <Typography component='li' variant='body2'>
                      Assessment: {waveformData.assessment}
                    </Typography>
                  </Box>
                </Box>
                <ECGPlot
                  strokeColor={STROKE_COLOR}
                  samples={waveformData.samples}
                  decreaseWave={0}
                  sampleRate={waveformData.sampleRate}
                  durationToShow={undefined}
                  millivolts={undefined}
                  startTime={5}
                  showGrid={true}
                  signalStrokeWidth={1}
                  smallGridStrokeWidth={0.21}
                  bigGridStrokeWidth={0.4}
                  physicalGridSquareSize={1}
                  full={undefined}
                  backgroundFill='#fff'
                  cellColor={GRID_COLOR}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
